export const DIMENSION_KEYS = [
  'understanding',
  'verification',
  'questionDesign',
  'independentThinking',
  'collaboration',
] as const;

export type DimensionKey = (typeof DIMENSION_KEYS)[number];
export type Mode = 'student' | 'teacher' | 'professional';
export type Zone = 'strong' | 'verify' | 'human';
export type ResultTypeId = 'passenger' | 'operator' | 'navigator' | 'collaborator';
export type LevelId = 'beginner' | 'user' | 'navigator' | 'strategist' | 'creator';

export type DimensionScores = Record<DimensionKey, number>;

export interface ManifestLocale {
  code: string;
  label: string;
  htmlLang: string;
  direction: 'ltr' | 'rtl';
  regionModel: string;
  uiPath: string;
  scenariosPath: string;
  capabilitiesPath: string;
}

export interface ContentManifest {
  schemaVersion: number;
  contentVersion: string;
  compatibleClient: string;
  updatedAt: string;
  defaultLocale: string;
  modelPath: string;
  locales: ManifestLocale[];
}

export interface ModelChoice {
  id: string;
  scores: DimensionScores;
}

export interface ModelScenario {
  id: string;
  construct: string;
  modes: Mode[];
  boundary: {
    reviewClass: 'model-sensitive' | 'durable-responsibility' | 'mixed';
    humanConstant: string;
    modelAssumption: string;
    reviewAfter: string;
  };
  realism: {
    decisionOwner: Mode;
    stakes: string;
    constraint: string;
  };
  choices: ModelChoice[];
}

export interface AssessmentModel {
  schemaVersion: number;
  modelVersion: string;
  assessmentLength: number;
  dimensions: DimensionKey[];
  boundaryPolicy: {
    version: number;
    principle: string;
    reviewCadenceMonths: number;
  };
  benchmark: {
    kind: 'assessment-reference';
    labelRequired: boolean;
    points: Array<{ score: number; percentile: number }>;
  };
  types: Array<{ id: ResultTypeId; min: number; max: number }>;
  levels: Array<{ id: LevelId; number: number; min: number }>;
  scenarios: ModelScenario[];
}

export interface ScenarioMetadata {
  createdAt: string;
  lastUpdated: string;
  applicableAiGeneration: string;
  difficulty: 'easy' | 'medium' | 'hard';
  markets: string[];
}

export interface LocalizedChoice {
  id: string;
  text: string;
  effect: string;
}

export interface LocalizedScenario {
  id: string;
  eyebrow: string;
  scenario: string;
  aiPrompt: string;
  aiAnswer: string;
  question: string;
  insight: string;
  check: string;
  choices: LocalizedChoice[];
  metadata: ScenarioMetadata;
}

export interface ScenarioPack {
  schemaVersion: number;
  packVersion: string;
  locale: string;
  regionModel: string;
  scenarios: LocalizedScenario[];
}

export interface ScenarioChoice extends LocalizedChoice, ModelChoice {}

export interface Scenario extends Omit<LocalizedScenario, 'choices'> {
  construct: string;
  modes: Mode[];
  choices: ScenarioChoice[];
}

export interface CapabilityItem {
  id: string;
  title: string;
  zone: Zone;
  stakes: number;
  uncertainty: number;
  roles: Mode[];
  summary: string;
  why: string;
  move: string;
  sourceIds?: string[];
}

export interface CapabilityReview {
  asOf: string;
  title: string;
  summary: string;
  note: string;
  sources: Array<{
    id: string;
    title: string;
    url: string;
    publishedAt: string;
    summary: string;
  }>;
}

export interface CapabilityPack {
  schemaVersion: number;
  packVersion: string;
  locale: string;
  items: CapabilityItem[];
  review?: CapabilityReview;
}

export interface TypeCopy {
  title: string;
  description: string;
  recommendation: string;
  /** One short imperative that fits the card's next-move line. */
  cardMove: string;
}

export interface LevelCopy {
  title: string;
  description: string;
}

export interface UiCopy {
  schemaVersion: number;
  locale: string;
  brand: { name: string; strap: string; language: string };
  loading: { label: string; errorTitle: string; errorBody: string; retry: string };
  assessment: Record<string, string>;
  modes: Record<Mode, { label: string; description: string }>;
  feedback: { strong: string; mixed: string; risk: string; principle: string; check: string };
  results: Record<string, string>;
  boundary: {
    overline: string;
    title: string;
    subtitle: string;
    filterAll: string;
    zones: Record<Zone, { title: string; subtitle: string }>;
    stakesLow: string;
    stakesHigh: string;
    uncertainty: string;
    humanStakes: string;
    why: string;
    move: string;
    back: string;
    takeTest: string;
    /** Axis and orientation copy for the capability field plot. */
    mapAxisX: string;
    mapAxisY: string;
    mapHint: string;
    sources: string;
  };
  shared: Record<string, string>;
  dimensions: Record<DimensionKey, string>;
  types: Record<ResultTypeId, TypeCopy>;
  levels: Record<LevelId, LevelCopy>;
  recommendations: Record<DimensionKey, string[]>;
  share: {
    cardEyebrow: string;
    score: string;
    challenge: string;
    anonymous: string;
    text: string;
    typeLabel: string;
    strongestLabel: string;
    nextMoveLabel: string;
    roleLabel: string;
    levelLabel: string;
    referenceLabel: string;
    serialLabel: string;
    series: string;
    boundaryTitle: string;
    testTypeLabel: string;
    cardTitle: string;
  };
}

export interface AppContent {
  manifest: ContentManifest;
  locale: ManifestLocale;
  model: AssessmentModel;
  ui: UiCopy;
  scenarios: Scenario[];
  capabilities: CapabilityItem[];
  capabilityReview?: CapabilityReview;
}

export interface Answer {
  scenarioId: string;
  choiceId: string;
}

export interface AssessmentResult {
  total: number;
  dimensions: DimensionScores;
  typeId: ResultTypeId;
  levelId: LevelId;
  levelNumber: number;
  percentile: number;
  strongest: DimensionKey;
  growthEdge: DimensionKey;
}

export interface LocalProgress {
  version: 1;
  attempts: number;
  bestScore: number;
  highestLevel: number;
  completedScenarioIds: string[];
  updatedAt: string;
}
